import React, { useState } from 'react';
import PropTypes, { any } from 'prop-types';

import Filter, { FilterModal, FilterSection } from '../../../SearchPage/Filters/Filter';

import styles from '../../../SearchPage/search.scss';
import { divisionFormatTypeIds, gameTypeValues } from '../../../../utils/eventConstants';
import DebouncedFilterInput from '../../../../components/FormElementsStandalone/DebouncedFilterInput';
import Collapsible from '../../../../components/Form/Collapsible';
import SingleCheckbox from '../../../SearchPage/Filters/FilterComponents/CoreComponents/SingleCheckbox';
import DropdownCheckBoxFilter from './DropdownCheckBoxFilter';

const DrawFilter = (props) => {
    const { filters, filterModal, handleFilterClick, closeModals, filterInputs, handleFilterSubmit, handleFilterUpdate, mobileOnly, desktopOnly, draws = [], 'data-testid': dataTestId } = props;

    const [filter, setFilter] = useState('');
    const [isSinglesOpen, setIsSinglesOpen] = useState(true);
    const [isDoublesOpen, setIsDoublesOpen] = useState(true);

    const singlesIsSelected = filterInputs?.playerType === gameTypeValues.SINGLES;
    const doublesIsSelected = filterInputs?.playerType === gameTypeValues.DOUBLES;

    const filterString = filter.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
    const filterRegex = new RegExp(filterString, 'i');

    const filteredDraws = draws.filter((draw) => {
        if (filter && !draw.name?.match(filterRegex)) {
            return false;
        }
        return true;
    });

    const singlesDraws = filteredDraws.filter((draw) => {
        return draw.formatTypeId === divisionFormatTypeIds.SINGLES;
    }).map(draw => ({ label: draw.name, value: draw.id }));

    const doublesDraws = filteredDraws.filter((draw) => {
        return draw.formatTypeId !== divisionFormatTypeIds.SINGLES;
    }).map(draw => ({ label: draw.name, value: draw.id }));

    const addOrRemoveId = (id) => {
        let updated;
        if (filterInputs.selectedDraws.includes(id)) {
            updated = filterInputs.selectedDraws.filter(item => item !== id);
        } else {
            updated = [...filterInputs.selectedDraws, id];
        }
        return updated;
    };

    const updateFilter = (value) => {
        setFilter(value);
    };

    const handleAllSinglesClick = () => {
        let updated;
        if (filterInputs.selectedDraws.length === singlesDraws.length && (!filterInputs.playerType || filterInputs.playerType === gameTypeValues.SINGLES)) {
            updated = [];
        } else {
            updated = singlesDraws.map(draw => draw.value);
        }
        handleFilterUpdate({
            selectedDraws: updated,
            playerType: updated.length ? gameTypeValues.SINGLES : '',
            notInDraw: false,
        });
    };

    const isSinglesAllSelected = filterInputs.selectedDraws.length === singlesDraws.length;

    const handleAllDoublesClick = () => {
        let updated;
        if (filterInputs.selectedDraws.length === doublesDraws.length && (!filterInputs.playerType || filterInputs.playerType === gameTypeValues.DOUBLES)) {
            updated = [];
        } else {
            updated = doublesDraws.map(draw => draw.value);
        }
        handleFilterUpdate({
            selectedDraws: updated,
            playerType: updated.length ? gameTypeValues.DOUBLES : '',
            notInDraw: false,
        });
    };

    const handleNotInDrawClick = () => {
        if (filters.notInDraw) {
            handleFilterUpdate({
                selectedDraws: [],
                notInDraw: false,
            });
        } else {
            handleFilterUpdate({
                selectedDraws: [],
                notInDraw: true,
            });
        }
    };

    const isDoublesAllSelected = filterInputs.selectedDraws.length === doublesDraws.length;

    const handleCollapseSingles = () => {
        setIsSinglesOpen(!isSinglesOpen);
    };

    const handleCollapseDoubles = () => {
        setIsDoublesOpen(!isDoublesOpen);
    };

    const filterName = 'selectedDraws';

    return draws.length ? (
        <div className={styles.filterWrapper}>
            <Filter
                filterName={filterName}
                handleFilterClick={handleFilterClick}
                isActive={!!filters?.selectedDraws?.length || filters?.notInDraw || filterModal === filterName}
                mobileOnly={mobileOnly}
                desktopOnly={desktopOnly}
                data-testid={dataTestId}
            >
                <div className={styles.flexContainer}>
                    <span className={styles.textStyle} data-testid={dataTestId && `${dataTestId}.label`}>
                        All draws {filters.selectedDraws.length ? `(${filters.selectedDraws.length})` : ''}
                    </span>
                    <i className={`uts uts-expand ${styles.iconStyle}`} />
                </div>
            </Filter>
            {filterModal === filterName && (
                <FilterModal
                    title={'Selected Draws'}
                    handleFilterSubmit={handleFilterSubmit}
                    onCloseClick={closeModals}
                    data-testid={dataTestId}
                >
                    <FilterSection
                        style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                        }}
                    >
                        <DebouncedFilterInput
                            value={filter}
                            onChange={updateFilter}
                            placeholder="Filter Draws"
                            data-testid={dataTestId && `${dataTestId}.search-input`}
                        />
                        {!doublesIsSelected && (
                            <>
                                <Collapsible
                                    isOpen={isSinglesOpen}
                                    onClick={handleCollapseSingles}
                                    data-testid={dataTestId && `${dataTestId}.singles-collapsible`}
                                >
                                    <SingleCheckbox
                                        id="singles"
                                        label={`SINGLES (${singlesDraws.length})`}
                                        onChange={handleAllSinglesClick}
                                        isChecked={isSinglesAllSelected}
                                        data-testid={dataTestId && `${dataTestId}.singles-select-all`}
                                    />
                                </Collapsible>
                                {isSinglesOpen && (
                                    <div className="ml24">
                                        <DropdownCheckBoxFilter
                                            options={singlesDraws}
                                            filterInputs={filterInputs}
                                            filterInputName={filterName}
                                            onChange={(value) => {
                                                handleFilterUpdate({
                                                    selectedDraws: addOrRemoveId(value),
                                                    playerType: gameTypeValues.SINGLES,
                                                    notInDraw: false,
                                                });
                                            }}
                                            isArray
                                            data-testid={dataTestId && `${dataTestId}.singles-options`}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {!singlesIsSelected && (
                            <>
                                <Collapsible
                                    isOpen={isDoublesOpen}
                                    onClick={handleCollapseDoubles}
                                    data-testid={dataTestId && `${dataTestId}.doubles-collapsible`}
                                >
                                    <SingleCheckbox
                                        id="doubles"
                                        label={`DOUBLES (${doublesDraws.length})`}
                                        onChange={handleAllDoublesClick}
                                        isChecked={isDoublesAllSelected}
                                        data-testid={dataTestId && `${dataTestId}.doubles-select-all`}
                                    />
                                </Collapsible>
                                {isDoublesOpen && (
                                    <div className="ml24">
                                        <DropdownCheckBoxFilter
                                            options={doublesDraws}
                                            filterInputs={filterInputs}
                                            filterInputName={filterName}
                                            onChange={(value) => {
                                                handleFilterUpdate({
                                                    selectedDraws: addOrRemoveId(value),
                                                    playerType: gameTypeValues.DOUBLES,
                                                    notInDraw: false,
                                                });
                                            }}
                                            isArray
                                            data-testid={dataTestId && `${dataTestId}.doubles-options`}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        <SingleCheckbox
                            id="not_in_draw"
                            label="NOT IN A DRAW"
                            onChange={handleNotInDrawClick}
                            isChecked={filterInputs?.notInDraw}
                            data-testid={dataTestId && `${dataTestId}.not-in-draw`}
                        />
                    </FilterSection>
                </FilterModal>
            )}
        </div>
    ) : null;
};

DrawFilter.propTypes = {
    filters: PropTypes.objectOf(any).isRequired,
    filterModal: PropTypes.objectOf(any).isRequired,
    handleFilterClick: PropTypes.func.isRequired,
    closeModals: PropTypes.func.isRequired,
    filterInputs: PropTypes.objectOf(any).isRequired,
    handleFilterUpdate: PropTypes.func.isRequired,
    handleFilterSubmit: PropTypes.func.isRequired,
    mobileOnly: PropTypes.bool,
    desktopOnly: PropTypes.bool,
    draws: PropTypes.arrayOf(any),
    'data-testid': PropTypes.string,
};

DrawFilter.defaultProps = {
    mobileOnly: false,
    desktopOnly: false,
    draws: [],
    'data-testid': undefined,
};

export default DrawFilter;
